import { useMemo } from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import LinkIcon from '@mui/icons-material/Link';
import MouseIcon from '@mui/icons-material/Mouse';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
  Box,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { Link } from 'react-router-dom';
import {
  DataTable,
  MetricCard,
  PageHeader,
  SectionCard,
  LoadingState,
  StatusChip,
} from '@/components/shared';
import { GoogleAdsCampaign, GoogleAdsOverview } from '@/services/googleAds';
import { Column } from '@/trio-ui/DataTable';

export type DatePreset =
  | 'this_month'
  | 'last_month'
  | 'this_quarter'
  | 'this_year'
  | 'last_year'
  | 'custom';
export type DateFilter = { preset: DatePreset; startDate: string; endDate: string };

type Props = {
  isConnected: boolean;
  overview: GoogleAdsOverview;
  isLoading: boolean;
  isSyncing: boolean;
  onSync: () => void;
  dateFilter: DateFilter;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDateChange: (startDate: string, endDate: string) => void;
};

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  ENABLED: { label: 'Active', color: '#10B981' },
  PAUSED: { label: 'Paused', color: '#F59E0B' },
  REMOVED: { label: 'Removed', color: '#EF4444' },
  UNKNOWN: { label: 'Unknown', color: '#71717A' },
};

const fmt = (n: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

const fmtFull = (n: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const num = (n: number): string => n.toLocaleString();

const columns: Column<GoogleAdsCampaign>[] = [
  {
    id: 'campaignName',
    label: 'Campaign',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, fontSize: 13 }}>{row.campaignName}</Box>
        {row.campaignType && <Box sx={{ fontSize: 11, color: '#71717A' }}>{row.campaignType}</Box>}
      </Box>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (row) => <StatusChip status={row.status} statusMap={STATUS_MAP} />,
  },
  {
    id: 'impressions',
    label: 'Impressions',
    align: 'right',
    render: (row) => num(row.impressions),
  },
  {
    id: 'clicks',
    label: 'Clicks',
    align: 'right',
    render: (row) => num(row.clicks),
  },
  {
    id: 'cost',
    label: 'Cost',
    align: 'right',
    render: (row) => <Box sx={{ fontFamily: 'monospace' }}>{fmt(row.cost)}</Box>,
  },
  {
    id: 'conversions',
    label: 'Conv.',
    align: 'right',
    render: (row) => (
      <Box>
        <Box sx={{ fontWeight: 500, color: row.conversions > 0 ? '#10B981' : 'inherit' }}>
          {num(row.conversions)}
        </Box>
        <Box sx={{ fontSize: 11, color: '#71717A' }}>{row.cvr.toFixed(1)}%</Box>
      </Box>
    ),
  },
  {
    id: 'cpc',
    label: 'CPC',
    align: 'right',
    render: (row) => (
      <Box sx={{ fontFamily: 'monospace' }}>{row.clicks > 0 ? fmtFull(row.cpc) : '—'}</Box>
    ),
  },
  {
    id: 'cpa',
    label: 'CPA',
    align: 'right',
    render: (row) => (
      <Box
        sx={{
          fontFamily: 'monospace',
          fontWeight: 500,
          color: row.cpa > 200 ? '#EF4444' : 'inherit',
        }}
      >
        {row.conversions > 0 ? fmtFull(row.cpa) : '—'}
      </Box>
    ),
  },
  {
    id: 'ctr',
    label: 'CTR',
    align: 'right',
    render: (row) => `${row.ctr.toFixed(1)}%`,
  },
];

function formatLastSynced(syncedAt: string | null): string {
  if (!syncedAt) return '';
  const diff = Date.now() - new Date(syncedAt).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(syncedAt).toLocaleDateString();
}

const CEOxGoogleAdsComponent = function ({
  isConnected,
  overview,
  isLoading,
  isSyncing,
  onSync,
  dateFilter,
  onPresetChange,
  onCustomDateChange,
}: Props): JSX.Element {
  const dateFilterActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      {overview.lastSyncedAt && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 12 }}>
          Synced {formatLastSynced(overview.lastSyncedAt)}
        </Typography>
      )}
      <Tooltip title={isSyncing ? 'Syncing...' : 'Refresh data from Google Ads'}>
        <IconButton
          size="small"
          onClick={onSync}
          disabled={isSyncing}
          sx={{
            color: '#71717A',
            '&:hover': { color: '#054AD9' },
            animation: isSyncing ? 'spin 1s linear infinite' : 'none',
            '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } },
          }}
        >
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <ToggleButtonGroup
        value={dateFilter.preset}
        exclusive
        onChange={(_, val) => val && onPresetChange(val)}
        size="small"
        sx={{
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontSize: 13,
            px: 1.5,
            py: 0.5,
            borderColor: '#E4E4E7',
            '&.Mui-selected': {
              backgroundColor: '#054AD915',
              color: '#054AD9',
              borderColor: '#054AD9',
              '&:hover': { backgroundColor: '#054AD925' },
            },
          },
        }}
      >
        <ToggleButton value="this_month">This Month</ToggleButton>
        <ToggleButton value="last_month">Last Month</ToggleButton>
        <ToggleButton value="this_quarter">This Quarter</ToggleButton>
        <ToggleButton value="this_year">YTD</ToggleButton>
        <ToggleButton value="last_year">Last Year</ToggleButton>
        <ToggleButton value="custom">Custom</ToggleButton>
      </ToggleButtonGroup>
      {dateFilter.preset === 'custom' && (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            type="date"
            size="small"
            value={dateFilter.startDate}
            onChange={(e) => onCustomDateChange(e.target.value, dateFilter.endDate)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
          <TextField
            type="date"
            size="small"
            value={dateFilter.endDate}
            onChange={(e) => onCustomDateChange(dateFilter.startDate, e.target.value)}
            sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
          />
        </Box>
      )}
    </Box>
  );

  // Spend vs Conversions chart
  const spendChartOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params
            .map((p: any) =>
              p.seriesName === 'Cost'
                ? `${p.marker} ${p.seriesName}: ${fmt(p.value)}`
                : `${p.marker} ${p.seriesName}: ${p.value}`
            )
            .join('<br/>'),
      },
      legend: {
        data: ['Cost', 'Conversions'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#71717A' },
      },
      grid: { left: 70, right: 40, top: 20, bottom: 44 },
      xAxis: {
        type: 'category',
        data: overview.campaigns.map((c) =>
          c.campaignName.length > 15 ? `${c.campaignName.slice(0, 15)}...` : c.campaignName
        ),
        axisLabel: { fontSize: 10, color: '#71717A', rotate: 30 },
        axisLine: { lineStyle: { color: '#E4E4E7' } },
      },
      yAxis: [
        {
          type: 'value',
          name: 'Cost',
          axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => fmt(v) },
          splitLine: { lineStyle: { color: '#F4F4F5' } },
        },
        {
          type: 'value',
          name: 'Conversions',
          axisLabel: { fontSize: 11, color: '#71717A' },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: 'Cost',
          type: 'bar',
          data: overview.campaigns.map((c) => c.cost),
          itemStyle: { color: '#4285F4', borderRadius: [4, 4, 0, 0] },
        },
        {
          name: 'Conversions',
          type: 'line',
          yAxisIndex: 1,
          data: overview.campaigns.map((c) => c.conversions),
          lineStyle: { color: '#10B981', width: 2 },
          itemStyle: { color: '#10B981' },
          symbol: 'circle',
          symbolSize: 6,
          smooth: true,
        },
      ],
    }),
    [overview.campaigns]
  );

  // Campaign performance bar chart (top by CTR)
  const topCampaigns = useMemo(
    () =>
      [...overview.campaigns]
        .filter((c) => c.impressions > 0)
        .sort((a, b) => b.ctr - a.ctr)
        .slice(0, 10),
    [overview.campaigns]
  );

  const perfChartOption = useMemo(
    () => ({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) =>
          params.map((p: any) => `${p.marker} ${p.seriesName}: ${p.value}%`).join('<br/>'),
      },
      legend: {
        data: ['CTR', 'CVR'],
        bottom: 0,
        textStyle: { fontSize: 12, color: '#71717A' },
      },
      grid: { left: 140, right: 20, top: 10, bottom: 40 },
      yAxis: {
        type: 'category',
        data: topCampaigns.map((c) =>
          c.campaignName.length > 25 ? `${c.campaignName.slice(0, 25)}...` : c.campaignName
        ),
        axisLabel: { fontSize: 11, color: '#71717A' },
        inverse: true,
      },
      xAxis: {
        type: 'value',
        axisLabel: { fontSize: 11, color: '#71717A', formatter: (v: number) => `${v}%` },
        splitLine: { lineStyle: { color: '#F4F4F5' } },
      },
      series: [
        {
          name: 'CTR',
          type: 'bar',
          data: topCampaigns.map((c) => Number(c.ctr.toFixed(2))),
          itemStyle: { color: '#4285F4', borderRadius: [0, 4, 4, 0] },
          barWidth: 12,
        },
        {
          name: 'CVR',
          type: 'bar',
          data: topCampaigns.map((c) => Number(c.cvr.toFixed(2))),
          itemStyle: { color: '#10B981', borderRadius: [0, 4, 4, 0] },
          barWidth: 12,
        },
      ],
    }),
    [topCampaigns]
  );

  if (!isConnected) {
    return (
      <>
        <PageHeader title="Google Ads" actions={dateFilterActions} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            p: 2,
            borderRadius: '8px',
            backgroundColor: '#FEF3C7',
            border: '1px solid #FDE68A',
          }}
        >
          <LinkIcon sx={{ fontSize: 18, color: '#D97706' }} />
          <Box sx={{ fontSize: 13, color: '#92400E', flex: 1 }}>
            Connect your <strong>Google Ads</strong> account to track campaign performance
            automatically.
          </Box>
          <Link
            to="/settings/integrations"
            style={{ fontSize: 13, color: '#054AD9', textDecoration: 'none', fontWeight: 500 }}
          >
            Connect now
          </Link>
        </Box>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Google Ads" actions={dateFilterActions} />
        <LoadingState message="Loading Google Ads data..." />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Google Ads" actions={dateFilterActions} />

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <MetricCard
          label="Total Spend"
          value={fmt(overview.totalCost)}
          subtitle={`${num(overview.totalImpressions)} impressions`}
          color="#4285F4"
          indicator={<AttachMoneyIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="Conversions"
          value={num(overview.totalConversions)}
          subtitle={`${fmtFull(overview.totalConversionValue)} value`}
          color="#10B981"
          indicator={<TrackChangesIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="CPA"
          value={overview.totalConversions > 0 ? fmtFull(overview.avgCpa) : '—'}
          subtitle={`CPC: ${overview.totalClicks > 0 ? fmtFull(overview.avgCpc) : '—'}`}
          color={overview.avgCpa > 200 ? '#EF4444' : '#054AD9'}
          indicator={<MouseIcon sx={{ fontSize: 16 }} />}
        />
        <MetricCard
          label="CTR"
          value={`${overview.avgCtr.toFixed(1)}%`}
          subtitle={`${num(overview.totalClicks)} clicks · CVR: ${overview.avgCvr.toFixed(1)}%`}
          color={overview.avgCtr >= 3 ? '#10B981' : '#D97706'}
          indicator={<VisibilityIcon sx={{ fontSize: 16 }} />}
        />
      </Box>

      {/* Charts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 2,
          mb: 3,
        }}
      >
        <SectionCard title="Spend vs Conversions" subtitle="By campaign">
          {overview.campaigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No campaign data yet.
            </Box>
          ) : (
            <ReactECharts option={spendChartOption} style={{ height: 280 }} />
          )}
        </SectionCard>

        <SectionCard title="Campaign Performance" subtitle="Top campaigns by CTR">
          {topCampaigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', fontSize: 13, color: '#71717A' }}>
              No campaign data yet.
            </Box>
          ) : (
            <ReactECharts
              option={perfChartOption}
              style={{ height: Math.max(280, topCampaigns.length * 40 + 60) }}
            />
          )}
        </SectionCard>
      </Box>

      {/* Campaigns table */}
      <DataTable
        columns={columns}
        data={overview.campaigns}
        rowKey={(r) => r.campaignId}
        searchPlaceholder="Search campaigns..."
        searchFields={(r) => r.campaignName}
        emptyMessage="No campaigns found."
      />
    </>
  );
};

export default CEOxGoogleAdsComponent;
